import type { ChangeEvent } from 'react';

// Image handling utilities
export const createImagePreviews = (images: File[]): string[] => {
  return images.map((file) => URL.createObjectURL(file));
};

export const handleImageUpload = (
  e: ChangeEvent<HTMLInputElement>,
  currentImages: File[],
  setImages: (images: File[]) => void,
  maxImages = 5,
) => {
  const files = e.target.files;
  if (!files) {
    return;
  }

  const fileArray = Array.from(files);
  const newImages = [...currentImages, ...fileArray];

  // Respect max images limit
  const limitedImages = newImages.slice(0, maxImages);
  setImages(limitedImages);

  // Clear the input to allow re-uploading the same file
  e.target.value = '';
};

export const removeImage = (index: number, currentImages: File[], setImages: (images: File[]) => void) => {
  const newImages = currentImages.filter((_, i) => i !== index);
  setImages(newImages);
};

// Cleanup object URLs to prevent memory leaks
export const cleanupImagePreviews = (previews: string[]) => {
  previews.forEach((url) => {
    if (url.startsWith('blob:')) {
      URL.revokeObjectURL(url);
    }
  });
};

// Validate image files
export const validateImageFiles = (files: FileList | null): { valid: boolean; errors: string[] } => {
  if (!files) {
    return { valid: true, errors: [] };
  }

  const errors: string[] = [];
  const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
  const maxSize = 10 * 1024 * 1024; // 10MB

  Array.from(files).forEach((file, index) => {
    if (!validTypes.includes(file.type)) {
      errors.push(`File ${index + 1}: Only JPEG, PNG, and WebP images are allowed`);
    }

    if (file.size > maxSize) {
      errors.push(`File ${index + 1}: File size must be less than 10MB`);
    }
  });

  return {
    valid: errors.length === 0,
    errors,
  };
};
